import { Head, router } from "@inertiajs/react"
import {
  BookOpen,
  ChevronDown,
  FileText,
  Link2,
  Plus,
  RefreshCw,
  RotateCw,
  StickyNote,
  Trash2,
  X,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"

import HeadingSmall from "@/components/heading-small"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Empty } from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { cn } from "@/lib/utils"
import {
  resyncSettingsKnowledgeItemPath,
  settingsBusinessProfilePath,
  settingsKnowledgeItemPath,
  settingsKnowledgeItemsPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [{ title: "Settings", href: "/settings" }]

type KbItemType = "document" | "url" | "note"
type ModalType = "url" | "note" | null

interface KbItem {
  uid: string
  kind: KbItemType
  name: string
  byte_size?: number | null
  body?: string | null
  source_url?: string | null
  last_synced_at?: string | null
  error?: string | null
  sync_status: "syncing" | "synced" | "failed"
  created_at: string
}

// A document upload in flight (or failed) — client-only until it lands as a
// persisted item. Mirrors the design's optimistic add (IMP-372 AC5/AC6).
interface PendingUpload {
  tempId: string
  name: string
  byteSize: number
  status: "uploading" | "failed"
  file: File
}

const ACCEPT = ".pdf,.doc,.docx,.txt,.csv,.md"

function getCsrfToken(): string {
  return (
    document
      .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  })
}

function ItemIcon({ type }: { type: KbItemType }) {
  const Icon = type === "url" ? Link2 : type === "note" ? StickyNote : FileText
  return (
    <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-md">
      <Icon className="text-muted-foreground size-4" />
    </div>
  )
}

function BusinessProfileRow() {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-white px-4 py-3 dark:bg-transparent">
      <ItemIcon type="document" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">Business profile</p>
        <p className="text-muted-foreground text-sm">Always included</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground shrink-0"
        asChild
      >
        <a href={settingsBusinessProfilePath()}>Manage</a>
      </Button>
    </div>
  )
}

function PendingUploadRow({
  upload,
  onRetry,
  onRemove,
}: {
  upload: PendingUpload
  onRetry: (tempId: string) => void
  onRemove: (tempId: string) => void
}) {
  const failed = upload.status === "failed"
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-white px-4 py-3 dark:bg-transparent",
        failed && "border-destructive/40",
      )}
    >
      <ItemIcon type="document" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{upload.name}</p>
        {failed ? (
          <p className="text-destructive text-xs">
            Upload failed — try again or remove it.
          </p>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-500">
            <Spinner className="size-3.5" />
            Uploading…
          </span>
        )}
      </div>
      {failed && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onRetry(upload.tempId)}
            title="Retry"
          >
            <RotateCw className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive h-8 w-8"
            onClick={() => onRemove(upload.tempId)}
            title="Remove"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

function itemSubtitle(item: KbItem): string {
  if (item.kind === "document") {
    return [formatBytes(item.byte_size), formatDate(item.created_at)]
      .filter(Boolean)
      .join(" · ")
  }
  if (item.kind === "url") {
    return item.last_synced_at
      ? `Last synced ${formatDate(item.last_synced_at)}`
      : "Not synced yet"
  }
  return formatDate(item.created_at)
}

function KbItemRow({
  item,
  onDelete,
  onRefresh,
}: {
  item: KbItem
  onDelete: (item: KbItem) => void
  onRefresh: (item: KbItem) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const syncing = item.sync_status === "syncing"
  const failed = item.sync_status === "failed"

  const subtitle = syncing ? (
    <span className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-500">
      <Spinner className="size-3.5" />
      Syncing…
    </span>
  ) : failed ? (
    <p className="text-destructive text-xs">
      {item.kind === "url"
        ? "Sync failed. Enter another link or try again."
        : "Upload failed"}
    </p>
  ) : (
    <p className="text-muted-foreground text-sm">{itemSubtitle(item)}</p>
  )

  if (item.kind === "note" && item.body) {
    return (
      <Collapsible
        open={expanded}
        onOpenChange={setExpanded}
        className="rounded-lg border bg-white dark:bg-transparent"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <ItemIcon type={item.kind} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{item.name}</p>
            {subtitle}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ChevronDown
                  className={cn(
                    "size-3.5 transition-transform",
                    expanded && "rotate-180",
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive h-8 w-8"
              onClick={() => onDelete(item)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
        <CollapsibleContent>
          <div className="border-t px-4 py-3">
            <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap">
              {item.body}
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    )
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-white px-4 py-3 dark:bg-transparent">
      <ItemIcon type={item.kind} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{item.name}</p>
        {subtitle}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {item.kind === "url" && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onRefresh(item)}
            title="Re-sync"
            disabled={syncing}
          >
            <RefreshCw className="size-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive h-8 w-8"
          onClick={() => onDelete(item)}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  )
}

function NoteModal({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (body: string) => Promise<void>
}) {
  const [body, setBody] = useState("")
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!body.trim() || saving) return
    setSaving(true)
    try {
      await onSave(body.trim())
      setBody("")
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setBody("")
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Write a note</DialogTitle>
          <DialogDescription>
            Paste or type anything your agents should know — policies, FAQs,
            pricing, seasonal changes.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="Your agents will have access to everything you write here…"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={!body.trim() || saving}
          >
            {saving ? "Saving…" : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function UrlModal({
  open,
  onOpenChange,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (url: string) => Promise<void>
}) {
  const [url, setUrl] = useState("")
  const [saving, setSaving] = useState(false)

  async function handleAdd() {
    if (!url.trim() || saving) return
    setSaving(true)
    try {
      await onAdd(url.trim())
      setUrl("")
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setUrl("")
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a URL</DialogTitle>
          <DialogDescription>
            We&rsquo;ll fetch this page and add it to the knowledge base. One
            page per URL — you can re-sync it later to refresh.
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder="https://yoursite.com/about"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void handleAdd()}
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleAdd()}
            disabled={!url.trim() || saving}
          >
            {saving ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  itemName,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  itemName: string
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove from knowledge base?</AlertDialogTitle>
          <AlertDialogDescription>
            <strong>{itemName}</strong> will be removed. Your agents won&rsquo;t
            have access to it anymore.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default function KnowledgeBase({ items }: { items: KbItem[] }) {
  const [modal, setModal] = useState<ModalType>(null)
  const [deleteTarget, setDeleteTarget] = useState<KbItem | null>(null)
  const [pending, setPending] = useState<PendingUpload[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Poll while anything is mid-sync so the syncing → synced flip shows without a
  // manual refresh (IMP-372 AC5).
  useEffect(() => {
    if (!items.some((i) => i.sync_status === "syncing")) return
    const timer = setInterval(() => {
      router.reload({ only: ["items"] })
    }, 4000)
    return () => clearInterval(timer)
  }, [items])

  function refreshItems() {
    router.reload({ only: ["items"] })
  }

  async function uploadFile(file: File, tempId: string) {
    const formData = new FormData()
    formData.append("kind", "document")
    formData.append("file", file)
    try {
      const res = await fetch(settingsKnowledgeItemsPath(), {
        method: "POST",
        headers: { "X-CSRF-Token": getCsrfToken(), Accept: "application/json" },
        body: formData,
        credentials: "same-origin",
      })
      if (!res.ok) throw new Error("upload failed")
      setPending((prev) => prev.filter((u) => u.tempId !== tempId))
      refreshItems()
    } catch {
      setPending((prev) =>
        prev.map((u) => (u.tempId === tempId ? { ...u, status: "failed" } : u)),
      )
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    Array.from(files).forEach((file) => {
      const tempId = `${Date.now()}-${file.name}-${Math.random()}`
      setPending((prev) => [
        ...prev,
        {
          tempId,
          name: file.name,
          byteSize: file.size,
          status: "uploading",
          file,
        },
      ])
      void uploadFile(file, tempId)
    })
  }

  function handleRetry(tempId: string) {
    const upload = pending.find((u) => u.tempId === tempId)
    if (!upload) return
    setPending((prev) =>
      prev.map((u) =>
        u.tempId === tempId ? { ...u, status: "uploading" } : u,
      ),
    )
    void uploadFile(upload.file, tempId)
  }

  function handleRemovePending(tempId: string) {
    setPending((prev) => prev.filter((u) => u.tempId !== tempId))
  }

  async function handleSaveNote(body: string) {
    const res = await fetch(settingsKnowledgeItemsPath(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ kind: "note", body }),
      credentials: "same-origin",
    })
    if (res.ok) refreshItems()
  }

  async function handleAddUrl(url: string) {
    const res = await fetch(settingsKnowledgeItemsPath(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ kind: "url", url }),
      credentials: "same-origin",
    })
    if (res.ok) refreshItems()
  }

  // IMP-374: re-fetch a URL item's page; the poll picks up the resulting state.
  function handleResync(item: KbItem) {
    void fetch(resyncSettingsKnowledgeItemPath(item.uid), {
      method: "POST",
      headers: { "X-CSRF-Token": getCsrfToken(), Accept: "application/json" },
      credentials: "same-origin",
    }).then(() => refreshItems())
  }

  function deleteItem(uid: string) {
    router.delete(settingsKnowledgeItemPath(uid), { preserveScroll: true })
  }

  // A failed item is deleted straight away (no confirmation modal); everything
  // else asks first.
  function handleDeleteRequest(item: KbItem) {
    if (item.sync_status === "failed") deleteItem(item.uid)
    else setDeleteTarget(item)
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return
    deleteItem(deleteTarget.uid)
    setDeleteTarget(null)
  }

  const isEmpty = items.length === 0 && pending.length === 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Knowledge base" />

      {/* Page-level picker so "Upload a file" opens it directly — no modal. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files)
          e.target.value = ""
        }}
      />

      <SettingsLayout>
        <div className="space-y-4">
          <div className="flex items-end justify-between">
            <HeadingSmall
              title="Knowledge base"
              description="Everything your assistant knows about your business."
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-1.5 size-3.5" />
                  Add
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <FileText className="mr-2 size-4" />
                  Upload a file
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setModal("url")}>
                  <Link2 className="mr-2 size-4" />
                  Add a URL
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setModal("note")}>
                  <StickyNote className="mr-2 size-4" />
                  Write a note
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-2">
            <BusinessProfileRow />

            {pending.map((upload) => (
              <PendingUploadRow
                key={upload.tempId}
                upload={upload}
                onRetry={handleRetry}
                onRemove={handleRemovePending}
              />
            ))}

            {isEmpty ? (
              <div className="rounded-lg border">
                <Empty
                  icon={<BookOpen className="size-4" />}
                  title="Nothing else added yet"
                  description="Upload documents, add URLs, or write notes for your assistant to reference."
                />
              </div>
            ) : (
              items.map((item) => (
                <KbItemRow
                  key={item.uid}
                  item={item}
                  onDelete={handleDeleteRequest}
                  onRefresh={handleResync}
                />
              ))
            )}
          </div>
        </div>
      </SettingsLayout>

      <UrlModal
        open={modal === "url"}
        onOpenChange={(o) => !o && setModal(null)}
        onAdd={handleAddUrl}
      />
      <NoteModal
        open={modal === "note"}
        onOpenChange={(o) => !o && setModal(null)}
        onSave={handleSaveNote}
      />
      <DeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        itemName={deleteTarget?.name ?? ""}
      />
    </AppLayout>
  )
}
